import {
	getTextureFormat,
	getTextureInternalFormat,
	getTextureType,
	TextureFormat,
} from "@/core/backends/webgl/texture-format"
import { TextureDataOptions, TextureFilter, TextureOptions, TextureWrap } from "./texture"
import { uuid } from "@/utils/uuid"
import { getBytesPerPixel } from "./texture-size"
import { WebGLBackend } from "../webgl-backend"

export interface Texture3DOptions extends TextureOptions {
	label: string
	depth?: number
}

export interface Texture3DDataOptions extends TextureDataOptions {
	depth?: number
}

export class Texture3D {
	static framebuffer: WebGLFramebuffer | null = null
	static readFramebuffer: WebGLFramebuffer | null = null

	private backend: WebGLBackend

	private get gl() {
		return this.backend.gl
	}

	id: string
	label: string

	texture: WebGLTexture
	format: TextureFormat
	width: number
	height: number
	depth: number

	constructor(backend: WebGLBackend, options: Texture3DOptions = { label: "Unnamed" }) {
		this.id = uuid()
		this.backend = backend
		this.texture = createTexture3D(this.gl, options)
		this.format = options.format ?? "rgba8unorm"
		this.label = options.label

		if (ArrayBuffer.isView(options.data) || options.data == null) {
			this.width = options.width ?? 0
			this.height = options.height ?? 0
			this.depth = options.depth ?? 0
		} else {
			this.width = 0
			this.height = 0
			this.depth = 0
		}

		this.backend.textureMemoryCounters.add(this)
	}

	bind(unit: number) {
		this.gl.activeTexture(this.gl.TEXTURE0 + unit)
		this.gl.bindTexture(this.gl.TEXTURE_3D, this.texture)

		this.backend.textureMemoryCounters.update(this)
	}

	unbind(unit: number) {
		this.gl.activeTexture(this.gl.TEXTURE0 + unit)
		this.gl.bindTexture(this.gl.TEXTURE_3D, null)
	}

	update(options: Texture3DDataOptions) {
		this.width = options.width ?? this.width
		this.height = options.height ?? this.height
		this.depth = options.depth ?? this.depth
		this.format = options.format ?? this.format

		this.gl.activeTexture(this.gl.TEXTURE0)
		this.gl.bindTexture(this.gl.TEXTURE_3D, this.texture)

		setTextureData3D(this.gl, {
			width: this.width,
			height: this.height,
			depth: this.depth,
			format: this.format,
			...options,
		})

		this.backend.textureMemoryCounters.update(this)
	}

	getBytesPerPixel() {
		const internalFormat = getTextureInternalFormat(this.gl, this.format)
		return getBytesPerPixel(this.gl, internalFormat)
	}

	getByteLength() {
		const size = this.width * this.height * this.depth
		return size * this.getBytesPerPixel()
	}

	updateSize(width: number, height: number, depth: number) {
		width = Math.floor(width)
		height = Math.floor(height)
		depth = Math.floor(depth)

		if (width === this.width && height === this.height && depth == this.depth) {
			return
		}

		this.width = width
		this.height = height
		this.depth = depth

		const internalFormat = getTextureInternalFormat(this.gl, this.format)
		const format = getTextureFormat(this.gl, this.format)
		const type = getTextureType(this.gl, this.format)

		this.gl.activeTexture(this.gl.TEXTURE0)
		this.gl.bindTexture(this.gl.TEXTURE_3D, this.texture)
		this.gl.texImage3D(
			this.gl.TEXTURE_2D,
			0,
			internalFormat,
			width,
			height,
			depth,
			0,
			format,
			type,
			null,
		)

		this.backend.textureMemoryCounters.update(this)
	}

	delete() {
		this.gl.deleteTexture(this.texture)

		this.backend.textureMemoryCounters.delete(this)
	}
}

function createTexture3D(
	gl: WebGL2RenderingContext,
	options: Texture3DOptions = { label: "Unnamed" },
) {
	const texture = gl.createTexture()

	if (texture == null) {
		throw new Error("Could not create texture")
	}

	const minFilter = getTextureFilter(options.min ?? "linear", options.mips)
	const magFilter = getTextureFilter(options.mag ?? "linear", false)
	const wrapS = getTextureWrap(options.wrapS ?? "clamp-to-edge")
	const wrapT = getTextureWrap(options.wrapT ?? "clamp-to-edge")

	gl.bindTexture(gl.TEXTURE_3D, texture)
	gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, minFilter)
	gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, magFilter)
	gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, wrapS)
	gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, wrapT)

	setTextureData3D(gl, options)

	return texture
}

function setTextureData3D(gl: WebGL2RenderingContext, options: Texture3DDataOptions) {
	gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE)
	gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, options.flipY ?? false)
	gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, options.premultiplyAlpha ?? false)
	gl.pixelStorei(gl.UNPACK_SKIP_ROWS, options.skipRows ?? 0)
	gl.pixelStorei(gl.UNPACK_ROW_LENGTH, options.rowLength ?? 0)

	const internalFormat = getTextureInternalFormat(gl, options.format)
	const format = getTextureFormat(gl, options.format)
	const type = getTextureType(gl, options.format)

	const width = options.width ?? 0
	const height = options.height ?? 0
	const depth = options.depth ?? 0

	if (ArrayBuffer.isView(options.data) || options.data == null) {
		gl.texImage3D(
			gl.TEXTURE_3D,
			0,
			internalFormat,
			width,
			height,
			depth,
			0,
			format,
			type,
			options.data ?? null,
		)
	} else {
		console.warn("[texture-3d] Must provide data to upload.")
	}
}

export function getTextureFilter(filter: TextureFilter, mips?: boolean) {
	const ctx = WebGL2RenderingContext

	switch (filter) {
		case "linear":
			return mips ? ctx.LINEAR_MIPMAP_LINEAR : ctx.LINEAR
		case "nearest":
			return mips ? ctx.NEAREST_MIPMAP_NEAREST : ctx.NEAREST
	}
}

export function getTextureWrap(wrap: TextureWrap) {
	const ctx = WebGL2RenderingContext

	switch (wrap) {
		case "clamp-to-edge":
			return ctx.CLAMP_TO_EDGE
		case "repeat":
			return ctx.REPEAT
	}
}
